import type { Polygon } from 'geojson';
import type { RequestType } from '~/common/types';
import { getAxiosClient } from './common';

const axios = getAxiosClient();

/** Four `[lng, lat]` pairs in Mapbox `image` source order: top-left, top-right, bottom-right, bottom-left. */
export type GeoreferencedMapCorners = [[number, number], [number, number], [number, number], [number, number]];

/** How the project reaches a map page: its own fulfilled data request, or an agency digitization. */
export type GeoreferencedMapAccess = 'requested' | 'digitized';

/** One map page under one agency; a page linked to several agencies' requests comes back once per agency. */
export interface GeoreferencedMap {
  id: string;
  access: GeoreferencedMapAccess;
  // The project's own request for this agency; null when reached only through digitization.
  dataRequestId: string | null;
  agencyId: string;
  agencyName: string;
  utilityType: string | null;
  mapOrAsbuilt: RequestType | null;
  sourceFileId: string;
  sourceFileName: string;
  pageNumber: number;
  corners: GeoreferencedMapCorners;
  footprint: Polygon;
  // Signed S3 URL; expires an hour after the response.
  pngUrl: string;
  // Signed S3 URL that opens the source PDF inline; expires an hour after the response.
  // Null for a digitized page or when it can't be signed.
  sourceFileUrl: string | null;
}

const PAGE_SIZE = 100;

/** Pages through `{ items, count }` responses and returns every map for the project. */
export async function getGeoreferencedMaps(projectId: string): Promise<GeoreferencedMap[]> {
  const maps: GeoreferencedMap[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data } = await axios({
      method: 'GET',
      url: `/v3/projects/${projectId}/georeferenced-maps`,
      params: { limit: PAGE_SIZE, offset },
    });
    maps.push(...data.items);
    if (data.items.length === 0 || maps.length >= data.count) return maps;
  }
}
